import { parseAndDecodeHeaders } from '../mime-headers/parseAndDecodeHeaders';
import { ParsedDecodedHeaders } from '../mime-headers/parseAndDecodeHeaders.types';
import { HeaderParseOptions } from '../mime-headers/parseHeaders.types';
import { getSingleHeader, getSingleHeaderValue } from '../mime-headers/utils';
import { StringDecoder } from '../types';
import { escapeRegExp, flatten, isStr } from '../utils/common';
import { escapeNonAscii } from '../utils/format';
import { Warning, WarningCode } from '../warnings';
import { knownPreambles } from './knownPreambles';
import { LeafMimeNode, MimeNode, MimeParsedMultipartBody, MultipartMimeNode } from './types';

const rxDoubleNewline = /\r?\n\r?\n/;

function normPreamble(str: string) {
	return str.toLowerCase().replace(/[\s\0\.>-]+/g, '');
}

const knownPreambles2 = new Set(Object.keys(knownPreambles).map(normPreamble));

export function parseMultipartMimeBody(
	body: string,
	boundary: string,
	decode: StringDecoder,
	treePos?: string
): MimeParsedMultipartBody {
	const selfWarnings: Warning[] = [];

	// First, split at close-delimiter

	// The close delimiter should always start with a newline, unless it is the only line in the body
	// The trailing newline is essentially optional
	const parts1 = body.split(
		new RegExp(`(?:\\r?\\n|^)--${escapeRegExp(boundary)}--(?:\\r?\\n|$)`, 'g')
	);
	if (parts1.length !== 2) {
		throw new Error('Found a multipart MIME body without close-delimiter');
	}

	const [middle, epilogue] = parts1;
	// Should always start with a newline... but sometimes that newline is missing
	// and is consumed by double newline after headers
	const parts2 = middle.split(
		new RegExp(`(?:\\r?\\n|^)--${escapeRegExp(boundary)}(?:\\r?\\n|$)`, 'g')
	);
	if (parts2.length <= 1) {
		selfWarnings.push({
			code: WarningCode.MIME_MULTIPART_0_BODIES,
			info: `Multipart MIME node has 0 bodies`,
		});
	}

	const [preamble, ...children] = parts2;

	const childNodes = children.map((c, idx) => {
		const childPos: string = treePos ? treePos + '.' + (idx + 1) : (idx + 1).toString();

		// Handle nodes without headers...
		let expectHeaders = true;

		if (c === '') {
			// No headers nor body?
			expectHeaders = false;
		} else {
			const startNewline = /^\r?\n/.exec(c);
			if (startNewline) {
				// Two newlines after the boundary... a part with no headers!
				c = c.slice(startNewline[0].length);
				expectHeaders = false;
			}
		}

		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		return parseMimeTree(c, decode, undefined, childPos, expectHeaders);
	});
	const descWarnings = flatten(childNodes.map(n => n.selfWarnings));

	if (preamble) {
		const trimmed = normPreamble(preamble);
		if (trimmed && !knownPreambles2.has(trimmed)) {
			selfWarnings.push({
				code: WarningCode.MIME_MULTIPART_PREAMBLE,
				info: `Multipart MIME node has non-empty preamble of ${
					preamble.length
				} bytes (${escapeNonAscii(preamble.slice(0, 512))})`,
			});
		}
	}

	if (epilogue) {
		const trimmed = normPreamble(epilogue);
		// Sometimes the epilogue is just all nulls
		if (trimmed) {
			selfWarnings.push({
				code: WarningCode.MIME_MULTIPART_EPILOGUE,
				info: `Multipart MIME node has non-empty epilogue of ${
					epilogue.length
				} bytes (${escapeNonAscii(epilogue.slice(0, 512))})`,
			});
		}
	}

	const node: MimeParsedMultipartBody = {
		selfWarnings: selfWarnings,
		allWarnings: [...selfWarnings, ...descWarnings],
		children: childNodes,
		preamble: preamble ? preamble : undefined,
		epilogue: epilogue ? epilogue : undefined,
	};

	return node;
}

export function parseMimeTree(
	mimePart: Buffer | string,
	decode: StringDecoder,
	rootHeaderParseOptions?: HeaderParseOptions,
	treePos?: string,
	expectHeaders = true
): MimeNode {
	mimePart = isStr(mimePart) ? mimePart : mimePart.toString('binary');

	let rawBody: string;
	let selfHeaderWarnings: Warning[];
	let parsedHeaders: ParsedDecodedHeaders | undefined;

	// Ensure headers is trimmed properly
	if (!expectHeaders) {
		// MIME part with no headers
		rawBody = mimePart;
		selfHeaderWarnings = [];
		parsedHeaders = {};
	} else {
		const firstDoubleNewlineMatch = rxDoubleNewline.exec(mimePart);
		if (!firstDoubleNewlineMatch) {
			// Headers only
			const result = parseAndDecodeHeaders(mimePart, decode, rootHeaderParseOptions);
			selfHeaderWarnings = result.warnings;
			parsedHeaders = result.result;

			rawBody = '';
		} else {
			const headersStr = mimePart.slice(0, firstDoubleNewlineMatch.index);
			const result = parseAndDecodeHeaders(headersStr, decode, rootHeaderParseOptions);
			selfHeaderWarnings = result.warnings;
			parsedHeaders = result.result;

			rawBody = mimePart.slice(
				firstDoubleNewlineMatch.index + firstDoubleNewlineMatch[0].length
			);
		}
	}

	let contentType: string | undefined;
	let contentTypeType: string | undefined;
	let contentTypeBoundary: string | undefined;
	let contentTypeName: string | undefined;
	let contentTypeCharset: string | undefined;

	let contentDisposition: string | undefined;
	let contentDispositionFilename: string | undefined;

	let contentLocation: string | undefined;

	const _contentType = getSingleHeader(parsedHeaders, 'content-type', 'throw');
	if (_contentType) {
		if (_contentType.value) {
			contentType = _contentType.value.toLowerCase().trim();
		}
		contentTypeType = _contentType.params?.['type'];
		contentTypeBoundary = _contentType.params?.['boundary'];
		contentTypeName = _contentType.params?.['name'];
		contentTypeCharset = _contentType.params?.['charset'];
	}

	const _contentDisposition = getSingleHeader(parsedHeaders, 'content-disposition', 'throw');
	if (_contentDisposition) {
		if (_contentDisposition.value) {
			contentDisposition = _contentDisposition.value.toLowerCase().trim();
		}
		contentDispositionFilename = _contentDisposition.params?.['filename'];
	}

	const contentId = getSingleHeaderValue(parsedHeaders, 'content-id', 'throw');
	const transferEncoding = getSingleHeaderValue(
		parsedHeaders,
		'content-transfer-encoding',
		'throw'
	);

	const _contentLocation = getSingleHeader(parsedHeaders, 'content-location', 'throw');
	if (_contentLocation) {
		if (_contentLocation.value) {
			contentLocation = _contentLocation.value.toLowerCase().trim();
		}
	}

	if (contentType?.startsWith('multipart/')) {
		if (!contentTypeBoundary) {
			throw new Error('Got a MIME multipart node with no boundary...');
		}

		// First, split at close-delimiter
		const parsedBody = parseMultipartMimeBody(rawBody, contentTypeBoundary, decode, treePos);

		const selfWarnings2 = [...selfHeaderWarnings, ...parsedBody.selfWarnings];
		const allWarnings = [...selfHeaderWarnings, ...parsedBody.allWarnings];

		const node: MultipartMimeNode = {
			...parsedBody,
			mimeNodeType: 'multipart',
			treePos: treePos ?? 'root',
			selfWarnings: selfWarnings2,
			allWarnings: allWarnings,
			headers: parsedHeaders,
			rawBodyLength: rawBody.length,

			contentType,
			contentTypeType,
			contentTypeBoundary,
			contentTypeName,
			contentTypeCharset,

			contentDisposition,

			contentDispositionFilename,
			contentId,
			transferEncoding,
			contentLocation,
		};
		return node;
	} else {
		const node: LeafMimeNode = {
			mimeNodeType: 'leaf',
			treePos: treePos ?? 'root',
			selfWarnings: selfHeaderWarnings,
			allWarnings: selfHeaderWarnings,
			headers: parsedHeaders,
			rawBodyLength: rawBody.length,
			rawBody: rawBody,

			contentType,
			contentTypeType,
			contentTypeBoundary,
			contentTypeName,
			contentTypeCharset,

			contentDisposition,
			contentDispositionFilename: contentDispositionFilename,
			contentId,
			transferEncoding,
			contentLocation,
		};

		return node;
	}
}
